import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Link2, Loader2, Check, X, ExternalLink, AlertCircle, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  {
    id: 'google',
    name: 'Google Forms',
    color: '#4285F4',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
    helpText: 'Paste an OAuth 2.0 access token with Google Drive read scope.',
    helpLink: 'https://console.cloud.google.com/apis/credentials',
    helpLinkText: 'Google Cloud Console',
  },
  {
    id: 'microsoft',
    name: 'Microsoft Forms',
    color: '#00A4EF',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 23 23">
        <path fill="#f35325" d="M1 1h10v10H1z"/>
        <path fill="#81bc06" d="M12 1h10v10H12z"/>
        <path fill="#05a6f0" d="M1 12h10v10H1z"/>
        <path fill="#ffba08" d="M12 12h10v10H12z"/>
      </svg>
    ),
    helpText: 'Paste a Microsoft Graph API access token with Files.Read and User.Read scopes.',
    helpLink: 'https://developer.microsoft.com/en-us/graph/graph-explorer',
    helpLinkText: 'Graph Explorer',
  },
  {
    id: 'typeform',
    name: 'Typeform',
    color: '#262627',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#262627">
        <path d="M1 2h22v4H1zM1 10h14v4H1zM1 18h18v4H1z"/>
      </svg>
    ),
    helpText: 'Paste a personal access token with Forms: Read scope.',
    helpLink: 'https://admin.typeform.com/user/tokens',
    helpLinkText: 'Typeform Tokens',
  },
];

export const FormPicker = ({ onSelectForm }) => {
  const { token } = useAuth();
  const [connected, setConnected] = useState({});
  const [connecting, setConnecting] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(null);
  const [forms, setForms] = useState({});
  const [loadingForms, setLoadingForms] = useState(null);
  const [errors, setErrors] = useState({});

  const fetchConnected = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/connected`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const map = {};
      for (const a of res.data) {
        map[a.platform] = true;
      }
      setConnected(map);
      for (const platform of Object.keys(map)) {
        fetchForms(platform);
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchConnected();
  }, [fetchConnected]);

  const handleConnect = async (platform) => {
    if (!tokenInput.trim()) {
      toast.error('Please enter an API token');
      return;
    }
    setConnecting(platform);
    setErrors((prev) => ({ ...prev, [platform]: null }));
    try {
      await axios.post(`${API}/integrations/connect`, {
        platform, token: tokenInput,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setConnected((prev) => ({ ...prev, [platform]: true }));
      setShowTokenInput(null);
      setTokenInput('');
      toast.success(`${PLATFORMS.find(p => p.id === platform)?.name} connected!`);
      fetchForms(platform);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Connection failed';
      setErrors((prev) => ({ ...prev, [platform]: msg }));
      toast.error(msg);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      await axios.delete(`${API}/integrations/${platform}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnected((prev) => ({ ...prev, [platform]: false }));
      setForms((prev) => ({ ...prev, [platform]: [] }));
      setErrors((prev) => ({ ...prev, [platform]: null }));
      toast.success(`${PLATFORMS.find(p => p.id === platform)?.name} disconnected`);
    } catch {}
  };

  const fetchForms = async (platform) => {
    setLoadingForms(platform);
    setErrors((prev) => ({ ...prev, [platform]: null }));
    try {
      const res = await axios.get(`${API}/integrations/${platform}/forms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForms((prev) => ({ ...prev, [platform]: res.data }));
    } catch (err) {
      setForms((prev) => ({ ...prev, [platform]: [] }));
      if (err.response?.status !== 404) {
        setErrors((prev) => ({ ...prev, [platform]: 'Could not load forms. Token may have expired.' }));
      }
    } finally {
      setLoadingForms(null);
    }
  };

  const allForms = Object.values(forms).flat();
  const activePlatform = PLATFORMS.find((p) => p.id === showTokenInput);

  return (
    <div className="space-y-4" data-testid="form-picker">
      {/* Platform connection cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {PLATFORMS.map((p) => (
          <div key={p.id}>
            <button
              onClick={() => {
                if (connected[p.id]) {
                  fetchForms(p.id);
                } else {
                  setShowTokenInput(showTokenInput === p.id ? null : p.id);
                  setTokenInput('');
                }
              }}
              data-testid={`platform-${p.id}`}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                connected[p.id]
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-[hsl(var(--border))] hover:border-[hsl(var(--ring))]'
              }`}
            >
              <div className="flex items-center gap-2">
                {p.icon}
                <span className="font-medium text-sm">{p.name}</span>
                {connected[p.id] && <Check className="h-4 w-4 text-emerald-600 ml-auto" />}
              </div>
            </button>
            {connected[p.id] && (
              <div className="flex items-center gap-2 mt-1 ml-1">
                <button
                  onClick={() => fetchForms(p.id)}
                  className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-1"
                  data-testid={`refresh-${p.id}`}
                  disabled={loadingForms === p.id}
                >
                  <RefreshCw className={`h-3 w-3 ${loadingForms === p.id ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <span className="text-[hsl(var(--border))]">|</span>
                <button
                  onClick={() => handleDisconnect(p.id)}
                  className="text-xs text-red-500 hover:underline"
                  data-testid={`disconnect-${p.id}`}
                >
                  Disconnect
                </button>
              </div>
            )}
            {errors[p.id] && connected[p.id] && (
              <div className="flex items-start gap-1.5 mt-1.5 ml-1" data-testid={`error-${p.id}`}>
                <AlertCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-600">{errors[p.id]}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Token input panel */}
      {showTokenInput && !connected[showTokenInput] && activePlatform && (
        <Card className="p-4 border border-[hsl(var(--border))]" data-testid="token-input-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Connect {activePlatform.name}
            </p>
            <button onClick={() => { setShowTokenInput(null); setTokenInput(''); setErrors(prev => ({...prev, [showTokenInput]: null})); }}>
              <X className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            </button>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">
            {activePlatform.helpText}
          </p>
          <a
            href={activePlatform.helpLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 mb-3"
            data-testid="token-help-link"
          >
            <ExternalLink className="h-3 w-3" />
            {activePlatform.helpLinkText}
          </a>

          {errors[showTokenInput] && (
            <div className="flex items-start gap-1.5 mb-3 p-2 rounded-lg bg-red-50 border border-red-200" data-testid="connect-error">
              <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-600">{errors[showTokenInput]}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              data-testid="platform-token-input"
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Paste your API token here"
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleConnect(showTokenInput)}
            />
            <Button
              data-testid="connect-platform-btn"
              onClick={() => handleConnect(showTokenInput)}
              disabled={connecting === showTokenInput || !tokenInput.trim()}
              size="sm"
              className="rounded-full bg-[hsl(var(--primary))] hover:bg-[#1e40af]"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            </Button>
          </div>
        </Card>
      )}

      {/* Forms list */}
      {allForms.length > 0 && (
        <div data-testid="forms-list">
          <Label className="text-sm font-medium mb-2 block">Your Forms ({allForms.length})</Label>
          <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1">
            {allForms.map((f) => (
              <button
                key={`${f.platform}-${f.id}`}
                onClick={() => onSelectForm(f)}
                data-testid={`form-option-${f.id}`}
                className="w-full p-3 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[#EFF6FF] text-left transition-all flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{f.title}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{f.url}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                  f.platform === 'google' ? 'bg-blue-50 text-blue-700' :
                  f.platform === 'microsoft' ? 'bg-cyan-50 text-cyan-700' :
                  'bg-zinc-100 text-zinc-700'
                }`}>
                  {f.platform === 'google' ? 'Google' : f.platform === 'microsoft' ? 'Microsoft' : 'Typeform'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingForms && (
        <div className="flex items-center justify-center py-4" data-testid="forms-loading">
          <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--muted-foreground))] ml-2">Loading forms...</span>
        </div>
      )}

      {Object.keys(connected).filter((k) => connected[k]).length > 0 && allForms.length === 0 && !loadingForms && (
        <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-2" data-testid="no-forms-message">
          No forms found in connected accounts. Try refreshing or check your token permissions.
        </p>
      )}
    </div>
  );
};
